import type {
  BulkAssignPayload,
  LinkedProduct,
  Product,
  ProductDraft,
  ProductExportResponse,
  ProductListParams,
  ProductListResponse,
  StoreType,
  VariationGroupItem,
} from '../../../types/product'
import { covaProductApis } from './cova'
import { flowhubProductApis } from './flowhub'
import { greenlineProductApis } from './greenline'
import { lightspeedProductApis } from './lightspeed'

function resolveApis(storeType: StoreType) {
  switch (storeType) {
    case 'cova':
      return covaProductApis
    case 'greenline':
      return greenlineProductApis
    case 'lightspeed':
      return lightspeedProductApis
    case 'flowhub':
      return flowhubProductApis
    default:
      throw new Error(`Unknown store type: ${storeType}`)
  }
}

export const productApis = {
  getList: (
    storeId: string,
    storeType: StoreType,
    params: ProductListParams,
  ): Promise<ProductListResponse> =>
    resolveApis(storeType).getList(storeId, params),

  getDetail: (
    storeId: string,
    storeType: StoreType,
    productId: string,
  ): Promise<Product> => resolveApis(storeType).getDetail(storeId, productId),

  getVariationGroup: (
    storeId: string,
    storeType: StoreType,
    productId: string,
  ): Promise<VariationGroupItem[]> => {
    if (storeType === 'cova')
      return covaProductApis.getVariationGroup(storeId, productId)
    if (storeType === 'greenline')
      return greenlineProductApis.getVariationGroup(storeId, productId)
    return Promise.resolve([])
  },

  update: (
    storeId: string,
    storeType: StoreType,
    productId: string,
    data: Partial<ProductDraft>,
  ): Promise<Product> =>
    resolveApis(storeType).update(storeId, productId, data),

  // Triggers a POS resync for a single product — reuses the same detail endpoint as
  // `update()` but sends only `{ external_datasource: 'pos' }`, matching the old
  // ConnectDialog "sync" flow. Does not force manual like `update()` does.
  syncFromPos: (
    storeId: string,
    storeType: StoreType,
    productId: string,
  ): Promise<Product> => resolveApis(storeType).syncFromPos(storeId, productId),

  bulkUpdate: (
    storeId: string,
    storeType: StoreType,
    ids: string[],
    action: string,
  ): Promise<void> => resolveApis(storeType).bulkUpdate(storeId, ids, action),

  // Bulk "Map" — assign category/brand to many products. orgId is required for
  // the bulk endpoint envelope (verified against the real curl).
  bulkAssign: (
    storeId: string,
    storeType: StoreType,
    orgId: string,
    ids: string[],
    payload: BulkAssignPayload,
  ): Promise<void> =>
    resolveApis(storeType).bulkAssign(storeId, orgId, ids, payload),

  exportCsv: (
    storeId: string,
    storeType: StoreType,
    params: ProductListParams,
  ): Promise<ProductExportResponse> =>
    resolveApis(storeType).exportCsv(storeId, params),

  searchForLinking: (
    storeId: string,
    storeType: StoreType,
    excludeProductId: string,
    term: string,
  ): Promise<LinkedProduct[]> => {
    if (storeType === 'cova')
      return covaProductApis.searchForLinking(storeId, excludeProductId, term)
    if (storeType === 'greenline')
      return greenlineProductApis.searchForLinking(
        storeId,
        excludeProductId,
        term,
      )
    return Promise.resolve([])
  },

  // Coupon-flow product search — returns products with their `type` and
  // `variations` populated so the coupon `applied_products` payload can be
  // assembled without a second fetch. Only Cova has a dedicated variations
  // endpoint today; other store types fall back to the regular product list
  // and surface products without variation metadata (mapper sends
  // `type: 'variable', variations: []` as a defensive default).
  searchProductsWithVariations: async (
    storeId: string,
    storeType: StoreType,
    params: { search: string; locationIds?: (number | string)[] },
  ): Promise<
    Array<{
      id: string
      name: string
      type: 'simple' | 'variable'
      variations: Array<{ id: string }>
    }>
  > => {
    if (storeType === 'cova') {
      return covaProductApis.searchProductsWithVariations(storeId, params)
    }
    // Generic fallback for non-Cova store types until they grow their own
    // variation-aware endpoints. Surfaces products with empty variations —
    // good enough for adapters that don't enforce the Cova shape.
    const response = await resolveApis(storeType).getList(storeId, {
      page: 1,
      limit: 10,
      search: params.search,
    })
    return response.data.map((p) => ({
      id: p.id,
      name: p.name,
      type: 'variable' as const,
      variations: [],
    }))
  },
}

export { covaProductApis } from './cova'
export { greenlineProductApis } from './greenline'
export { lightspeedProductApis } from './lightspeed'
export { flowhubProductApis } from './flowhub'
